import React, { useState, useRef } from 'react';
import type { SpottedEvent } from '@/api/resultsApi';
import { getConfidenceColor } from '@/utils/confidenceColor';
import { formatTime } from '@/utils/formatTime';

// Mappa event type → colore del marker
const EVENT_COLORS: Record<string, string> = {
  serve: '#6366f1',   // indigo
  pass: '#8b5cf6',    // violet
  set: '#a78bfa',     // purple
  attack: '#ef4444',  // red
  block: '#f97316',   // orange
  dig: '#22c55e',     // green
  free_ball: '#06b6d4', // cyan
  tip: '#f59e0b',     // amber
};

// Abbreviazioni per ogni tipo di evento
const EVENT_SHORT: Record<string, string> = {
  serve: 'S',
  pass: 'P',
  set: 'A',
  attack: 'AT',
  block: 'M',
  dig: 'D',
  free_ball: 'FB',
  tip: 'TP',
};

interface EventMarkerProps {
  event: SpottedEvent;
  onClick?: (event: SpottedEvent) => void;
  style?: React.CSSProperties; // posizionamento assoluto dalla timeline
}

/**
 * Marker cliccabile per un evento sulla timeline del video.
 * Mostra una piccola barra colorata con l'abbreviazione del tipo di evento.
 * Al passaggio del mouse appare un tooltip con dettagli (tipo, timestamp, confidenza).
 */
const EventMarker: React.FC<EventMarkerProps> = ({ event, onClick, style }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const markerRef = useRef<HTMLDivElement>(null);

  const color = EVENT_COLORS[event.type] || '#94a3b8';
  const confidenceColor = getConfidenceColor(event.confidence);
  const short = EVENT_SHORT[event.type] || '?';

  return (
    <div
      ref={markerRef}
      style={{
        position: 'absolute',
        top: 0,
        width: '24px',
        height: '20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: onClick ? 'pointer' : 'default',
        zIndex: showTooltip ? 10 : 5,
        ...style,
      }}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.(event);
      }}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      {/* Barra colorata */}
      <div
        style={{
          width: '100%',
          height: '100%',
          backgroundColor: color,
          borderRadius: '2px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
        }}
      >
        <span style={{ color: 'white', fontSize: '0.55rem', fontWeight: 'bold', lineHeight: 1 }}>
          {short}
        </span>
      </div>

      {/* Tooltip */}
      {showTooltip && (
        <div
          style={{
            position: 'absolute',
            bottom: '28px',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: '#1e293b',
            color: 'white',
            padding: '0.5rem 0.75rem',
            borderRadius: '6px',
            fontSize: '0.75rem',
            whiteSpace: 'nowrap',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            pointerEvents: 'none',
            zIndex: 20,
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>
            {event.type.charAt(0).toUpperCase() + event.type.slice(1)} (Giocatore {event.player_id})
          </div>
          <div style={{ color: '#94a3b8' }}>
            {formatTime(event.timestamp_ms)} — Team {event.team}
          </div>
          <div style={{ color: confidenceColor, marginTop: '0.15rem' }}>
            Confidenza: {(event.confidence * 100).toFixed(0)}%
          </div>
        </div>
      )}
    </div>
  );
};

export default EventMarker;